import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import dateService from 'live/services/date'
import { cloneDeep } from 'lodash'
import LiveTooltip from 'live/components/ui/tooltip'
import { EmptyFields } from './analysisTypes'
import LiveSelect from 'live/components/forms/ui/select'
import { Asterisk } from '../common/components'

interface IProps {
    generalInfo: GeneralInfo
    setGeneralInfo: React.Dispatch<React.SetStateAction<GeneralInfo>>
    displayColumn?: boolean
    mostRecentEventTimeStamp: number
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}

export function DelayJustification({
    generalInfo,
    setGeneralInfo,
    displayColumn,
    mostRecentEventTimeStamp,
    emptyFields,
    setEmptyFields
}: IProps): JSX.Element {
    const [justificationState, setJustificationState] = useState<string>('')
    const [justificationObservationState, setJustificationObservationState] = useState<string>('')
    const justificationOptions = [
        { value: 'CLEANING', label: 'CLEANING' },
        { value: 'MAINTENANCE', label: 'MAINTENANCE' },
        { value: 'PREVENTIVE', label: 'PREVENTIVE' },
        { value: 'NO DEMAND', label: 'NO DEMAND' },
        { value: 'PROGRAM CHANGE', label: 'PROGRAM CHANGE' },
        { value: 'FREQUENCY FAILURE', label: 'FREQUENCY FAILURE' },
        { value: 'OTHERS', label: 'OTHERS' }
    ]
    function onChangeJustificationText(value: string): void {
        const localEmptyFields = emptyFields
        localEmptyFields.trigger = false
        localEmptyFields.fields = localEmptyFields.fields.filter(field => {
            return field !== 'justificationText'
        })
        setEmptyFields(localEmptyFields)
        setJustificationState('')
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.justificationText = value
        setGeneralInfo(updatedInfo)
    }

    function onChangeObeservationText(value: string): void {
        const localEmptyFields = emptyFields
        localEmptyFields.trigger = false
        localEmptyFields.fields = localEmptyFields.fields.filter(field => {
            return field !== 'justificationObservationText'
        })
        setEmptyFields(localEmptyFields)
        setJustificationObservationState('')
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.justificationObservationText = value
        setGeneralInfo(updatedInfo)
    }

    useEffect(() => {
        if (emptyFields.fields.includes('justificationText')) {
            setJustificationState('Alert')
        }
        if (emptyFields.fields.includes('justificationObservationText')) {
            setJustificationObservationState('Alert')
        }
    }, [emptyFields.fields, emptyFields.trigger])

    return (
        <StyledDelayJustification className={`DelayJustification ${displayColumn ? 'displayColumn' : ''}`}>
            <h1 className="DelayJustification--title">
                {i18n('delay')}{' '}
                <LiveTooltip
                    position="top"
                    content={i18n(
                        'Delay time for analysis. Put the justification for the delay in the justification field.'
                    )}
                    hide={false}
                >
                    <span className="DelayJustification--title__info">
                        <i className="far fa-info-circle"></i>
                    </span>
                </LiveTooltip>
            </h1>
            <div className="DelayJustification--inputs">
                <div className="DelayJustification--inputs__time">
                    <h3 className="DelayJustification--inputs__time--title">{i18n('last analysis')}</h3>
                    <span className="DelayJustification--inputs__time--value">
                        {dateService.fromNow(mostRecentEventTimeStamp)}
                    </span>
                </div>
                <div className="DelayJustification--inputs__rows">
                    <div className={`DelayJustification--inputs__reason${justificationState}`}>
                        <h3 className="DelayJustification--inputs__reason--title">
                            {i18n('justification')} <Asterisk />
                        </h3>
                        <LiveSelect
                            value={generalInfo?.justificationText}
                            style={{ border: 'none' }}
                            onChange={value => onChangeJustificationText(value)}
                            className={`DelayJustification--inputs__reason${justificationState}--input`}
                            placeholder={i18n('Add justification')}
                            options={justificationOptions.map(option => {
                                option.label = i18n(option.label)

                                return option
                            })}
                        />
                    </div>
                    <div className={`DelayJustification--inputs__reasonObservation${justificationObservationState}`}>
                        <h3 className="DelayJustification--inputs__reasonObservation--title">
                            {i18n('Observation')} {generalInfo?.justificationText === 'OTHERS' ? <Asterisk /> : null}
                        </h3>
                        <input
                            type="text"
                            value={generalInfo?.justificationObservationText}
                            onChange={event => onChangeObeservationText(event.target.value)}
                            className={`DelayJustification--inputs__reasonObservation${justificationObservationState}--input`}
                            placeholder={i18n('Add justification')}
                        />
                    </div>
                </div>
            </div>
        </StyledDelayJustification>
    )
}

const StyledDelayJustification = styled.div`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    border: var(--border);
    border-radius: 5px;
    width: 100%;
    display: flex;
    flex-direction: column;
    .DelayJustification {
        &--title {
            width: 100%;
            padding: 12px 24px;
            text-transform: uppercase;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-800']};
            border-bottom: var(--border);
            font-size: 16px;
            line-height: 16px;
            &__info {
                color: #37a3ef;
                font-size: 14px;
            }
        }
        &--inputs {
            &__rows {
                display: flex;
                flex-direction: row;
            }
            display: flex;
            flex-direction: column;
            &__reasonObservationAlert,
            &__reasonObservation {
                float: right;
            }
            &__reasonAlert,
            &__reasonObservationAlert,
            &__reasonObservation,
            &__reason {
                width: 50%;
                height: 95px;
            }
            &__reasonAlert,
            &__reasonObservationAlert,
            &__reasonObservation,
            &__time,
            &__reason {
                &,
                &:hover,
                .Select-control:hover {
                    box-shadow: none;
                }
                .Select-control {
                    .Select-input {
                        display: inline !important;
                    }
                    &,
                    input {
                        width: 100% !important;
                    }
                }
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                padding: 20px 24px 15px;
                border: 1px solid transparent;
                &--title {
                    text-transform: capitalize;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 15px;
                    line-height: 15px;
                    margin-bottom: 5px;
                    &__required {
                        color: #d9534f;
                        font: 'Source Sans Pro';
                    }
                }
                &:focus-within {
                    border: 1px solid ${({ theme }) => theme['blue-500']};
                }
            }
            &__time {
                border-bottom: var(--border);
                background: var(--gray-100);
                &--value {
                    height: 32px;
                    display: flex;
                    align-items: center;
                }
            }
            &__reasonAlert,
            &__reasonObservationAlert,
            &__reasonObservation,
            &__reason {
                &--input {
                    border: none;
                    &,
                    &:hover {
                        box-shadow: none;
                    }
                }
            }
            &__reasonAlert {
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                padding: 20px 24px 15px;
                border: 1px solid transparent;
                color: '#868695';
                &--title {
                    text-transform: capitalize;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 15px;
                    line-height: 15px;
                    margin-bottom: 5px;
                    &__required {
                        color: #d9534f;
                        font: 'Source Sans Pro';
                    }
                }
                &:focus-within {
                    border: 1px solid ${({ theme }) => theme['blue-500']};
                }
            }
            &__reasonAlert {
                &,
                &:hover,
                .Select-control:hover {
                    box-shadow: none;
                }
                .Select-control {
                    background: #fdd0cf;
                    .Select-input {
                        display: inline !important;
                    }
                    &,
                    input {
                        width: 100% !important;
                    }
                }
                background: #fdd0cf;
                &--input {
                    background: #fdd0cf;
                    border: none;
                    &,
                    &:hover {
                        box-shadow: none;
                    }
                }
            }
            &__reasonObservationAlert {
                background: #fdd0cf;

                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    padding: 20px 24px 15px;
                    border: 1px solid transparent;
                    color: '#868695';
                    &--title {
                        text-transform: capitalize;
                        font-family: ${({ theme }) => theme['font-semibold']};
                        color: ${({ theme }) => theme['gray-800']};
                        font-size: 15px;
                        line-height: 15px;
                        margin-bottom: 5px;
                        &__required {
                            color: #d9534f;
                            font: 'Source Sans Pro';
                        }
                    }
                    &:focus-within {
                        border: 1px solid ${({ theme }) => theme['blue-500']};
                    }
                }
                &__reasonObservationAlert {
                    &,
                    &:hover,
                    .Select-control:hover {
                        box-shadow: none;
                    }
                    .Select-control {
                        background: #fdd0cf;
                        .Select-input {
                            display: inline !important;
                        }
                        &,
                        input {
                            width: 100% !important;
                        }
                    }
                    &--input {
                        background: #fdd0cf;
                        border: none;
                        &,
                        &:hover {
                            box-shadow: none;
                        }
                    }
                }
            }
        }
    }
`
